import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MentorCard from './components/MentorCard';
import MentorFilters from './components/MentorFilters';
import SearchDropdown, { SuggestionItem } from '../../components/base/SearchDropdown';
import type { Mentor } from '../../mocks/mentors';
import { api } from '@/lib/api';
import { adaptLecturer } from '@/lib/adapters';

const SORTS = [
  { label: '評分最高', value: 'rating' },
  { label: '諮詢次數', value: 'sessions' },
  { label: '價格從低', value: 'price-asc' },
  { label: '價格從高', value: 'price-desc' }
] as const;

const PRICE_OPTIONS = [
  { label: '≤$200', value: '0-200' },
  { label: '$201–$250', value: '200-250' },
  { label: '$251–$300', value: '250-300' },
  { label: '$300+', value: '300+' }
] as const;

const MentorsPage = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [search, setSearch] = useState(initialQuery);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('rating');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const scrollToResults = useCallback(() => {
    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, []);

  useEffect(() => {
    void (async () => {
      const page = await api.searchLecturers({
        pageCurrent: 1,
        pageSize: 100,
        lecturerName: initialQuery || undefined
      });
      setMentors(page.list.map(adaptLecturer));
      setLoading(false);
    })();
  }, [initialQuery]);

  useEffect(() => {
    if (initialQuery) {
      scrollToResults();
    }
  }, [initialQuery, scrollToResults]);

  const categories = useMemo(
    () => Array.from(new Set(mentors.map((mentor) => mentor.category).filter(Boolean))),
    [mentors]
  );

  const filtered = useMemo(() => {
    let list = [...mentors];
    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter(
        (mentor) =>
          mentor.name.toLowerCase().includes(query) ||
          mentor.title.toLowerCase().includes(query) ||
          mentor.company.toLowerCase().includes(query) ||
          mentor.category.toLowerCase().includes(query) ||
          mentor.skills.some((skill) => skill.toLowerCase().includes(query))
      );
    }
    if (selectedCategory) {
      list = list.filter((mentor) => mentor.category === selectedCategory);
    }
    if (priceRange === '0-200') list = list.filter((mentor) => mentor.price <= 200);
    if (priceRange === '200-250') list = list.filter((mentor) => mentor.price > 200 && mentor.price <= 250);
    if (priceRange === '250-300') list = list.filter((mentor) => mentor.price > 250 && mentor.price <= 300);
    if (priceRange === '300+') list = list.filter((mentor) => mentor.price > 300);
    list.sort((left, right) => {
      if (sortBy === 'sessions') return right.sessions - left.sessions;
      if (sortBy === 'price-asc') return left.price - right.price;
      if (sortBy === 'price-desc') return right.price - left.price;
      return right.rating - left.rating;
    });
    return list;
  }, [mentors, priceRange, search, selectedCategory, sortBy]);

  const suggestions = useMemo((): SuggestionItem[] => {
    if (!search.trim()) return [];
    const query = search.toLowerCase();
    const items: SuggestionItem[] = [];
    const names = new Set<string>();
    const skills = new Set<string>();

    mentors.forEach((mentor) => {
      if (mentor.name.toLowerCase().includes(query) && !names.has(mentor.name)) {
        items.push({
          label: mentor.name,
          sub: `${mentor.title} · ${mentor.company}`,
          type: 'name',
          icon: 'ri-user-line',
          value: mentor.name
        });
        names.add(mentor.name);
      }
      mentor.skills.forEach((skill) => {
        if (skill.toLowerCase().includes(query) && !skills.has(skill)) {
          items.push({
            label: skill,
            sub: '技能標籤',
            type: 'skill',
            icon: 'ri-price-tag-3-line',
            value: skill
          });
          skills.add(skill);
        }
      });
    });

    return items.slice(0, 12);
  }, [mentors, search]);

  const baseList = useMemo(() => {
    let list = [...mentors];
    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter(
        (mentor) =>
          mentor.name.toLowerCase().includes(query) ||
          mentor.title.toLowerCase().includes(query) ||
          mentor.company.toLowerCase().includes(query) ||
          mentor.category.toLowerCase().includes(query) ||
          mentor.skills.some((skill) => skill.toLowerCase().includes(query))
      );
    }
    if (selectedCategory) {
      list = list.filter((mentor) => mentor.category === selectedCategory);
    }
    return list;
  }, [mentors, search, selectedCategory]);

  const priceCounts = useMemo(
    () => ({
      all: baseList.length,
      '0-200': baseList.filter((mentor) => mentor.price <= 200).length,
      '200-250': baseList.filter((mentor) => mentor.price > 200 && mentor.price <= 250).length,
      '250-300': baseList.filter((mentor) => mentor.price > 250 && mentor.price <= 300).length,
      '300+': baseList.filter((mentor) => mentor.price > 300).length
    }),
    [baseList]
  );

  const hotKeywords = useMemo(
    () =>
      Array.from(
        new Set(mentors.flatMap((mentor) => [mentor.category, ...mentor.skills]).filter(Boolean))
      ).slice(0, 5),
    [mentors]
  );

  const isFiltering = Boolean(search.trim() || selectedCategory || priceRange !== 'all');

  const activeChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; onRemove: () => void }> = [];
    if (search.trim()) {
      chips.push({ id: 'search', label: `"${search}"`, onRemove: () => setSearch('') });
    }
    if (selectedCategory) {
      chips.push({ id: 'category', label: selectedCategory, onRemove: () => setSelectedCategory(null) });
    }
    if (priceRange !== 'all') {
      const priceLabel = PRICE_OPTIONS.find((item) => item.value === priceRange)?.label;
      if (priceLabel) {
        chips.push({ id: 'price', label: priceLabel, onRemove: () => setPriceRange('all') });
      }
    }
    return chips;
  }, [priceRange, search, selectedCategory]);

  const resetAll = () => {
    setSearch('');
    setSelectedCategory(null);
    setPriceRange('all');
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <Navbar />

      <div style={{ paddingTop: '64px' }}>
        <div className="bg-[#0A1628] py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold text-violet-400 tracking-widest uppercase mb-3 block">
                全球頂尖 AI 導師
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight">
                遇見你的理想導師
              </h1>
              <p className="text-slate-400 text-sm leading-relaxed mb-6 sm:mb-8">
                平台匯聚來自全球頂尖外商公司的內部專家，每一位均通過 NexusGo 嚴格認證。
              </p>

              <SearchDropdown
                search={search}
                onSearchChange={(value) => {
                  setSearch(value);
                  if (value.trim()) {
                    scrollToResults();
                  }
                }}
                onClear={() => setSearch('')}
                suggestions={suggestions}
                onSuggestionClick={(item) => {
                  setSearch(item.value);
                  scrollToResults();
                }}
                placeholder="搜尋導師姓名、專業技能..."
                showDropdown={showDropdown}
                onShowDropdownChange={setShowDropdown}
                inputClassName="bg-white/10 text-white placeholder-slate-400 border-white/15 focus:border-violet-400 focus:bg-white/15"
              />
            </div>

            {!search.trim() && hotKeywords.length > 0 && (
              <div className="mt-5 sm:mt-6 flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-slate-500 text-xs font-medium whitespace-nowrap">熱門：</span>
                {hotKeywords.map((keyword) => (
                  <button
                    key={keyword}
                    onClick={() => {
                      setSearch(keyword);
                      scrollToResults();
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap bg-white/10 text-slate-300 hover:bg-white/20 border border-white/10"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            )}

            {categories.length > 0 && (
              <div className="mt-5 sm:mt-6 flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-slate-500 text-xs font-medium whitespace-nowrap">專業方向：</span>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory((current) => (current === category ? null : category));
                      scrollToResults();
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-violet-500 text-white'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/10'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center gap-2 sm:gap-3 flex-wrap">
              <span className="text-slate-500 text-xs font-medium whitespace-nowrap">諮詢價格：</span>
              {PRICE_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => {
                    setPriceRange((current) => (current === item.value ? 'all' : item.value));
                    scrollToResults();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    priceRange === item.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/10'
                  }`}
                >
                  {item.label}
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      priceRange === item.value
                        ? 'bg-white/25 text-white'
                        : 'bg-white/15 text-slate-400'
                    }`}
                  >
                    {priceCounts[item.value]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {isFiltering && (
          <div className="bg-violet-600 px-4 sm:px-6 py-2.5">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-white text-sm flex-wrap">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line text-violet-200 text-xs" />
                </div>
                <span className="text-violet-100 text-xs">
                  {search && <span>搜索「<strong className="text-white">{search}</strong>」</span>}
                  {selectedCategory && <span className="ml-1">· <strong className="text-white">{selectedCategory}</strong></span>}
                  {priceRange !== 'all' && (
                    <span className="ml-1">· <strong className="text-white">
                      {PRICE_OPTIONS.find((item) => item.value === priceRange)?.label}
                    </strong></span>
                  )}
                </span>
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full ml-1">
                  {filtered.length} 位導師
                </span>
              </div>
              <button
                onClick={resetAll}
                className="text-xs text-violet-200 hover:text-white cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors"
              >
                <i className="ri-close-line" />
                清除
              </button>
            </div>
          </div>
        )}

        <div ref={resultsRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 scroll-mt-16">
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowMobileFilters((value) => !value)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer transition-colors hover:border-violet-300 whitespace-nowrap"
            >
              <i className="ri-filter-3-line text-violet-500" />
              篩選條件
              {isFiltering && <span className="w-2 h-2 bg-violet-500 rounded-full" />}
              <i className={`ri-arrow-${showMobileFilters ? 'up' : 'down'}-s-line text-slate-400`} />
            </button>
            {showMobileFilters && (
              <div className="mt-3 bg-white rounded-xl border border-slate-200 p-4">
                <MentorFilters
                  categories={categories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={(value) => {
                    setSelectedCategory(value);
                    scrollToResults();
                    setShowMobileFilters(false);
                  }}
                  priceRange={priceRange}
                  setPriceRange={(value) => {
                    setPriceRange(value);
                    scrollToResults();
                  }}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  total={filtered.length}
                />
              </div>
            )}
          </div>

          <div className="flex gap-8 items-start">
            <div className="hidden lg:block w-52 flex-shrink-0">
              <MentorFilters
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={(value) => {
                  setSelectedCategory(value);
                  scrollToResults();
                }}
                priceRange={priceRange}
                setPriceRange={(value) => {
                  setPriceRange(value);
                  scrollToResults();
                }}
                sortBy={sortBy}
                setSortBy={setSortBy}
                total={filtered.length}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <div className="text-slate-600 text-sm flex items-center gap-2 flex-wrap">
                  找到{' '}
                  <span className="font-bold text-[#0A1628] text-base tabular-nums">
                    {filtered.length}
                  </span>{' '}
                  位導師
                  {selectedCategory && (
                    <span className="text-violet-600 font-medium">· {selectedCategory}</span>
                  )}
                </div>
                {isFiltering && (
                  <button
                    onClick={resetAll}
                    className="text-xs text-rose-500 hover:text-rose-600 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap transition-colors"
                  >
                    <i className="ri-close-circle-line" />
                    清除篩選
                  </button>
                )}
              </div>

              {activeChips.length > 0 && (
                <div className="mb-5 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400">已篩選：</span>
                  {activeChips.map((chip) => (
                    <span
                      key={chip.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full text-xs font-medium"
                    >
                      {chip.label}
                      <button
                        onClick={chip.onRemove}
                        className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-violet-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-close-line text-[10px]" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="overflow-hidden rounded-2xl border border-slate-100 bg-white"
                    >
                      <div className="h-52 animate-pulse bg-slate-100" />
                      <div className="space-y-3 p-5">
                        <div className="h-5 w-2/3 animate-pulse rounded-xl bg-slate-100" />
                        <div className="h-4 w-1/2 animate-pulse rounded-xl bg-slate-100" />
                        <div className="flex gap-2">
                          <div className="h-7 w-20 animate-pulse rounded-full bg-slate-100" />
                          <div className="h-7 w-16 animate-pulse rounded-full bg-slate-100" />
                        </div>
                        <div className="h-10 w-full animate-pulse rounded-xl bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                    <i className="ri-user-search-line text-slate-400 text-2xl" />
                  </div>
                  <p className="text-slate-500 text-sm font-medium mb-2">沒有找到符合條件的導師</p>
                  <p className="text-slate-400 text-xs mb-5">試試以下常見搜尋：</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {(hotKeywords.length ? hotKeywords : ['AI 研究', 'ML 工程', '產品管理', '系統設計', '英文面試']).map((keyword) => (
                      <button
                        key={keyword}
                        onClick={() => setSearch(keyword)}
                        className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs rounded-full cursor-pointer hover:border-violet-300 hover:text-violet-700 transition-colors whitespace-nowrap"
                      >
                        {keyword}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                  {filtered.map((mentor, index) => (
                    <div
                      key={mentor.id}
                      className="opacity-0 translate-y-3"
                      style={{
                        animation: 'fadeInUp 0.3s ease both',
                        animationDelay: `${index * 45}ms`
                      }}
                    >
                      <MentorCard mentor={mentor} highlight={search} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MentorsPage;
